import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import secureLocalStorage from 'react-secure-storage';
import api from '../services/api';
import { IUser, UserRole } from '../types';

interface IAuthContextData {
  user: IUser | null;
  currentRole: UserRole;
  setCurrentRole: (role: UserRole) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: SignUpData) => Promise<void>;
  signOut: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string, passwordConfirmation: string) => Promise<void>;
}

interface SignUpData {
  name: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
}

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [currentRole, setCurrentRole] = useState<UserRole>('user');
  const [isLoading, setIsLoading] = useState(true);

  // Carrega usuário do storage ao iniciar
  useEffect(() => {
    const loadUser = () => {
      const storedUser = secureLocalStorage.getItem('user');
      const storedRole = secureLocalStorage.getItem('currentRole') as UserRole;
      
      if (storedUser) {
        const userData = storedUser as IUser;
        setUser(userData);
        
        // Define a role atual (salva ou primeira disponível)
        if (storedRole && userData.roles.includes(storedRole)) {
          setCurrentRole(storedRole);
        } else {
          setCurrentRole(userData.roles[0]);
        }
      }
      setIsLoading(false);
    };

    loadUser();
  }, []);

  // Salva role atual no storage quando muda
  const handleSetCurrentRole = useCallback((role: UserRole) => {
    setCurrentRole(role);
    secureLocalStorage.setItem('currentRole', role);
  }, []);

  // Login
  const signIn = useCallback(async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: userData } = response.data.data;

    secureLocalStorage.setItem('token', token);
    secureLocalStorage.setItem('user', userData);
    
    setUser(userData);
    setCurrentRole(userData.roles[0]);
    secureLocalStorage.setItem('currentRole', userData.roles[0]);
  }, []);

  // Cadastro
  const signUp = useCallback(async (data: SignUpData) => {
    await api.post('/auth/register', data);
  }, []);

  // Logout
  const signOut = useCallback(() => {
    secureLocalStorage.removeItem('token');
    secureLocalStorage.removeItem('user');
    secureLocalStorage.removeItem('currentRole');
    setUser(null);
    setCurrentRole('user');
  }, []);

  // Recuperação de senha
  const forgotPassword = useCallback(async (email: string) => {
    await api.post('/auth/forgot-password', { email });
  }, []);

  // Reset de senha
  const resetPassword = useCallback(
    async (token: string, password: string, passwordConfirmation: string) => {
      await api.post('/auth/reset-password', {
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
    },
    []
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        currentRole,
        setCurrentRole: handleSetCurrentRole,
        isAuthenticated: !!user,
        isLoading,
        signIn,
        signUp,
        signOut,
        forgotPassword,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
